import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import type {
  FilterState,
  PaginationMeta,
  TableColumn,
} from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { useFetch } from '../../../hooks/useFetch';
import { EventInboxApiFactory } from '../api/eventInboxApi';
import type { EventInboxListItem } from '../eventInboxTypes';

interface UseGetEventInboxResult {
  data: EventInboxListItem[];
  meta: PaginationMeta | null;
  loading: boolean;
  error: ApiError | Error | null;
  refetch: () => void;
}

/**
 * Fetches a page of Event Inbox rows for the given filters/sort/pagination.
 * Server-side paginated: re-fetches whenever any input changes, aborting the
 * in-flight request so a fast page/filter change never races.
 */
export function useGetEventInbox(
  filters: FilterState<EventInboxListItem>,
  columns: TableColumn<EventInboxListItem>[],
  page: number,
  limit: number,
): UseGetEventInboxResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => EventInboxApiFactory(client), [client]);

  const [data, setData] = useState<EventInboxListItem[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [revision, setRevision] = useState(0);

  const refetch = useCallback(() => setRevision(r => r + 1), []);

  useEffect(() => {
    const ctrl = new AbortController();
    setError(null);

    api
      .getEvents(filters, columns, page, limit, { signal: ctrl.signal })
      .then(({ items, meta }) => {
        setData(items);
        setMeta(meta);
      })
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, JSON.stringify(filters), columns, page, limit, revision]);

  return { data, meta, loading, error, refetch };
}
